<template>
  <div class="files-list-container flex column gap30">
    <div class="files-list flex wrap gap20 width-all width-all">
      <div v-for="(file, idx) in filesToShow" :key="idx" class="flex column gap5 file-preview space-between" :class="{'width-all': ['video', 'iframe'].includes(cmpType), [`${cmpType}-section`]: true}">
        <template v-if="['video', 'img'].includes(cmpType)">
          <h5>
            {{file.title || ''}}
          </h5>
          <div class="flex align-center_ column gap5_ wrap">
            <p v-if="file.info">{{file.info}}</p>
            <p v-if="file.credit">{{$t('credit')}}: {{file.credit}}</p>
            <!-- <template v-if="cmpType === 'img'">
              <span v-if="file.info">|</span>
              <button class="btn clear underline" @click="downloadImg(fixFileSrcToThumbnail(file, rootItem), file.title)">{{$t('download')}}</button>
            </template> -->
          </div>
        </template>
        <p v-if="getFileError(file, rootItem)">{{getFileError(file, rootItem)}}</p>
        <iframe
          v-else-if="cmpType === 'iframe'"
          class="video-file-preview"
          :src="extractFileSrc({src: getFileSrc(file) || file?.src})" controls
        />
        <template v-else-if="cmpType === 'video'">
          <VideoTag
            :format="getFileItemFromRootItem(file, rootItem)?.format"
            class="video-file-preview"
            :src="fixVideoSrcToThumbnail(file, rootItem, organizationId)"
            :file-id="file.fileId || file._id"
            :organization-id="organizationId"
            :release-id="rootItem?._id"
            :fileItem="file"
            :compiledFileItem="getFileItemFromRootItem(file, rootItem)"
          />
          <p v-if="file.description" v-html="file.description"></p>
        </template>
        <FullScreenToggler v-else-if="cmpType === 'img'" class="width-content height-content">
          <div v-if="!getFileSrc(file) || !imageLoaded[file.fileId || file._id]" class="img-loader flex align-center justify-center">
            <div class="loader"></div>
          </div>
          <img
            v-if="getFileSrc(file)"
            v-show="imageLoaded[file.fileId || file._id]"
            class="img-file-preview content"
            :src="getFileSrc(file)" 
            :alt="file.title"
            @load="onImageLoad(file)"
            @error="onImageError(file)"
          />
          <div class="img-actions flex align-center gap10">
            <button class="btn download-btn hover-pop" @click="handleDownload(file)"><span>{{$t('download')}}</span></button>
          </div>
        </FullScreenToggler>
        <a
          v-else-if="cmpType === 'link'"
          class="link-file-preview"
          target="_blank" 
          :href="extractFileSrc(file)"
        >{{file.title}}</a>
        
        <router-link
          v-else-if="cmpType === 'file'"
          class="link-file-preview"
          target="_blank" 
          :to="{name: 'FileViewer', query: {file: getFileSrc(file) } }"
        >{{file.title}}</router-link>

      </div>
    </div>
    <PaginationBtns v-if="usePage && (files.length > 1)" v-model="paginationData" :total="files.length" :noLimitSelection="true"/>
  </div>
</template>

<script>
import FullScreenToggler from '../../common/cmps/FullScreenToggler.vue';
import PaginationBtns from '../../common/cmps/ItemSearchList/PaginationBtns.vue';
import { fixFileSrcToThumbnail, fixVideoSrcToThumbnail, getFileError, getFileItemFromRootItem, getFreshFileUrl } from '../../common/services/file.service';
import { Utils } from '../../common/services/util.service';
import VideoTag from './VideoTag.vue';
// import { extractFileSrc } from './file.service'; 
export default {
  name: 'FileList',
  components: {VideoTag, PaginationBtns, FullScreenToggler},
  props: {
    files: {
      type: Array
    },
    cmpType: {
      type: String,
      default: 'img'
    },
    organizationId: {
      type: String
    },
    rootItem: {
      type: Object
    },
  },
  methods: {
    fixFileSrcToThumbnail, getFileError, getFileItemFromRootItem,
    fixVideoSrcToThumbnail,
    downloadImg: Utils.downloadImg,
    extractFileSrc(fileItem) {
      let src = fileItem.src || fileItem.link || fileItem.url || '';
      // if (!/^https?:\/\//i.test(this.url)) src = `https://${src}`;
      if (!src.startsWith('https://') && !src.startsWith('http://')) src = `https://${src}`;
      if (this.cmpType === 'iframe') {
        if (Utils.youtubeService.isYoutubeVid(src)) src = Utils.youtubeService.embedUtubeUrl(src);
      }
      return src;
    },
    getFileSrc(file) {
      // Check if we have a regenerated URL for this file (use it first)
      if (this.regeneratedUrls[file.fileId || file._id]) {
        return this.regeneratedUrls[file.fileId || file._id];
      }
      
      // For hot-stream images, check if URL is expired
      if (this.cmpType === 'img') {
        const currentUrl = fixFileSrcToThumbnail(file, this.rootItem);
        if (currentUrl && currentUrl.includes('hot-stream.megaphon.co.il')) {
          try {
            const urlObj = new URL(currentUrl);
            const expiresParam = urlObj.searchParams.get('Expires');
            if (expiresParam) {
              const expiresTime = parseInt(expiresParam, 10) * 1000;
              const now = Date.now();
              // If expired or will expire in less than 2 seconds, trigger regeneration
              if (expiresTime <= now + 2000) {
                // Trigger regeneration in background
                this.regenerateUrlIfNeeded(file);
                // Return null to show loader until new URL is ready
                return null;
              }
            } else {
              // No Expires parameter - might be expired, regenerate
              this.regenerateUrlIfNeeded(file);
              // Return null to show loader until new URL is ready
              return null;
            }
          } catch (err) {
            // URL parsing failed, continue with current URL
          }
        }
      }
      
      // Otherwise use the cached URL
      return fixFileSrcToThumbnail(file, this.rootItem);
    },
    async handleDownload(file) {
      // For hot-stream files, always regenerate URL before downloading
      const currentUrl = fixFileSrcToThumbnail(file, this.rootItem);
      if (currentUrl && currentUrl.includes('hot-stream.megaphon.co.il') && file.fileId && this.organizationId) {
        try {
          const response = await getFreshFileUrl(file.fileId, this.organizationId, this.rootItem?._id);
          if (response.url) {
            // Use the fresh URL for download
            this.downloadFile(response.url, file.title);
            return;
          }
        } catch (err) {
          // Failed to regenerate, continue with current URL
        }
      }
      
      // For non-hot-stream files or if regeneration failed, use current URL
      const urlToDownload = this.getFileSrc(file) || currentUrl;
      this.downloadFile(urlToDownload, file.title);
    },
    downloadFile(url, fileName) {
      // Extract filename from URL if needed
      let finalFileName = fileName || 'download';
      
      // Remove .dropboxignore extension if present
      if (finalFileName.endsWith('.dropboxignore')) {
        finalFileName = finalFileName.replace(/\.dropboxignore$/, '');
      }
      
      // If filename doesn't have extension, try to extract from URL
      if (!finalFileName.includes('.')) {
        try {
          const urlObj = new URL(url);
          const pathname = urlObj.pathname;
          const lastSlash = pathname.lastIndexOf('/');
          const lastDot = pathname.lastIndexOf('.');
          if (lastDot > lastSlash && lastDot > -1) {
            const extension = pathname.substring(lastDot);
            finalFileName = finalFileName + extension;
          }
        } catch (err) {
          // Could not extract extension from URL
        }
      }
      
      // Use Utils.downloadImg to download
      Utils.downloadImg(url, finalFileName);
    },
    onImageLoad(file) {
      // Mark image as loaded
      this.$set(this.imageLoaded, file.fileId || file._id, true);
    },
    onImageError(file) {
      // If image fails to load, try to regenerate URL
      this.regenerateUrlIfNeeded(file);
      // Force component update to retry with new URL
      this.$forceUpdate();
    },
    async regenerateUrlIfNeeded(file) {
      // Only regenerate for hot-stream (private bucket) files
      const currentUrl = fixFileSrcToThumbnail(file, this.rootItem);
      if (!currentUrl || !file.fileId || !this.organizationId) return;
      
      // Check if URL is from hot-stream.megaphon.co.il (private bucket)
      if (!currentUrl.includes('hot-stream.megaphon.co.il')) return;
      
      // Check if URL has expired (has Expires parameter that's in the past)
      try {
        const urlObj = new URL(currentUrl);
        const expiresParam = urlObj.searchParams.get('Expires');
        if (expiresParam) {
          const expiresTime = parseInt(expiresParam, 10) * 1000; // Convert to milliseconds
          const now = Date.now();
          // If expired or will expire in less than 2 seconds, regenerate
          if (expiresTime <= now + 2000) {
            try {
              const response = await getFreshFileUrl(file.fileId, this.organizationId, this.rootItem?._id);
              if (response.url) {
                this.$set(this.regeneratedUrls, file.fileId || file._id, response.url);
                // Force component update so image can use new URL
                this.$forceUpdate();
              }
            } catch (err) {
              // Failed to regenerate file URL
            }
          }
        } else {
          // No Expires parameter - might be expired, regenerate anyway
          try {
            const response = await getFreshFileUrl(file.fileId, this.organizationId, this.rootItem?._id);
            if (response.url) {
              this.$set(this.regeneratedUrls, file.fileId || file._id, response.url);
              // Force component update so image can use new URL
              this.$forceUpdate();
            }
          } catch (err) {
            console.warn('Failed to regenerate file URL:', err);
          }
        }
      } catch (err) {
        // URL parsing failed, skip regeneration
      }
    }
  },
  data() {
    return {
      paginationData: {
        page: 0,
        limit: 1,
      },
      regeneratedUrls: {}, // Cache of regenerated URLs: { fileId: freshUrl }
      imageLoaded: {} // Track which images have loaded: { fileId: true/false }
    }
  },
  mounted() {
    // Regenerate URLs for all hot-stream images when component mounts
    if (this.cmpType === 'img') {
      this.files.forEach(async (file) => {
        const currentUrl = fixFileSrcToThumbnail(file, this.rootItem);
        if (currentUrl && currentUrl.includes('hot-stream.megaphon.co.il')) {
          // Pre-regenerate URL for hot-stream images
          await this.regenerateUrlIfNeeded(file);
        }
      });
    }
  },
  computed: {
    usePage() {
      return false;
      return this.cmpType === 'video';
    },
    filesToShow() {
      if (!this.usePage) return this.files;
      const startIdx = this.paginationData.limit * this.paginationData.page;
      const endIdx = startIdx + this.paginationData.limit;
      return this.files.slice(startIdx, endIdx);
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/global/index';
.files-list {
  width: 100%;
  .img-section {
    width: em(300px);
    h5 {
      width: em(300px);
      line-height: normal;
    }
  }
  .img-file-preview {
    width: em(300px);
    height: em(200px);
    object-fit: cover;
    max-width: 95vw;
    @media (max-width: $small-screen-break) {
    }
  }
  .img-loader {
    width: em(300px);
    height: em(200px);
    min-height: em(200px);
    background-color: rgba(0, 0, 0, 0.05);
    .loader {
      width: em(40px);
      height: em(40px);
      border: 3px solid rgba(0, 0, 0, 0.1);
      border-top-color: var(--clr-4, #2090d4);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .video-file-preview {
    box-shadow: $light-shadow;
    // height: em(600px);
    height: auto;
    width: 100%;
    max-width: 95vw;
  }
  .link-file-preview {
    // color: blue;
    text-decoration: underline !important;
    cursor: pointer;
  }
  .file-preview {
    position: relative;
    @media (max-width: $small-screen-break) {
      width: 100%;
      .img-file-preview, .video-file-preview {;
        width: 100%;
      }
      .img-file-preview {
        height: em(250px);
      }
    }
  }
  .img-actions {
    position: absolute;
    inset-inline-end: em(40px);
    top: em(10px);
    .btn {
      width: em(20px);
      height: em(20px);
      border-radius: 0;
      border: 1px solid var(--clr-0); // black
      // width: em(50px);
      // height: em(25px);
    }
  }

  @media (min-width: $small-screen-break) {
    iframe {
      height: 380px !important;
    }
  }

}
</style>